import React from 'react';
import { clsx } from 'clsx';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showFirstLast?: boolean;
  maxButtons?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = true,
  maxButtons = 5,
  size = 'md',
  className,
}) => {
  const getPageNumbers = () => {
    const delta = Math.floor(maxButtons / 2);
    const range: (number | string)[] = [];
    const rangeStart = Math.max(1, currentPage - delta);
    const rangeEnd = Math.min(totalPages, currentPage + delta);

    if (rangeStart > 1) {
      range.push(1);
      if (rangeStart > 2) range.push('...');
    }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      range.push(i);
    }

    if (rangeEnd < totalPages) {
      if (rangeEnd < totalPages - 1) range.push('...');
      range.push(totalPages);
    }

    return range;
  };

  const buttonClasses = clsx(
    'btn',
    size === 'xs' && 'btn-xs',
    size === 'sm' && 'btn-sm',
    size === 'lg' && 'btn-lg'
  );

  return (
    <div className={clsx('btn-group', className)}>
      {showFirstLast && (
        <button
          className={buttonClasses}
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          aria-label="First page"
        >
          «
        </button>
      )}

      <button
        className={buttonClasses}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        ‹
      </button>

      {getPageNumbers().map((page, index) => (
        <button
          key={index}
          className={clsx(
            buttonClasses,
            page === currentPage && 'btn-active',
            page === '...' && 'btn-disabled'
          )}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          disabled={page === '...'}
          aria-label={typeof page === 'number' ? `Page ${page}` : undefined}
          aria-current={page === currentPage ? 'page' : undefined}
        >
          {page}
        </button>
      ))}

      <button
        className={buttonClasses}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        ›
      </button>

      {showFirstLast && (
        <button
          className={buttonClasses}
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          aria-label="Last page"
        >
          »
        </button>
      )}
    </div>
  );
};
